/**
 * The site header's right side on every page: the account corner (Sign in, or My profile) and
 * the language switch. Both have their room in the page's HTML from the start and the corner
 * is drawn at once from the last known state, so nothing in the header moves when the session
 * check answers.
 */
import { rememberInvite } from "../http";
import { languageSwitch, t } from "../i18n";
import { inApp } from "../native";
import { initConsent } from "../ui/consent";

/** "1" when this browser was signed in last time (only a guess for the first paint). */
const HINT = "wc.signedIn";

function readHint(): boolean {
  try {
    return localStorage.getItem(HINT) === "1";
  } catch {
    return false;
  }
}

function writeHint(signedIn: boolean) {
  try {
    localStorage.setItem(HINT, signedIn ? "1" : "0");
  } catch {
    // Private mode: the next page guesses again.
  }
}

export interface SiteNav {
  /** Draws the corner for the session as it really is (and remembers it for the next page). */
  setSignedIn(signedIn: boolean): void;
}

/**
 * @param onSignIn opens the sign-in dialog on pages that have one; elsewhere the corner links
 * to the home page's dialog.
 */
export function mountNav(onSignIn?: () => void): SiteNav {
  // A friend's invite link (/?ref=CODE): kept for the sign-up.
  rememberInvite();
  // In the iOS app (profile, privacy, terms) the site's sections stay out of reach: the
  // website sells gem packs outside the App Store, so no link may lead there (site.css).
  if (inApp()) document.documentElement.classList.add("in-app");
  document.getElementById("nav-lang")?.replaceChildren(languageSwitch());
  // Every site page mounts the header: the cookie banner and analytics start here too.
  initConsent();
  const corner = document.getElementById("nav-account");
  const draw = (signedIn: boolean) => {
    if (!corner) return;
    if (signedIn) {
      const a = document.createElement("a");
      a.className = "nav-acc in";
      a.href = "/profile/";
      if (location.pathname.startsWith("/profile")) a.setAttribute("aria-current", "page");
      const dot = document.createElement("span");
      dot.className = "nav-acc-dot";
      dot.setAttribute("aria-hidden", "true");
      a.append(dot, t("My profile"));
      corner.replaceChildren(a);
    } else if (onSignIn) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "nav-acc";
      b.textContent = t("Sign in");
      b.onclick = onSignIn;
      corner.replaceChildren(b);
    } else {
      const a = document.createElement("a");
      a.className = "nav-acc";
      // In the app the game's title screen signs in; the website's would open in Safari.
      a.href = inApp() ? "/play/" : "/?auth=login";
      a.textContent = t("Sign in");
      corner.replaceChildren(a);
    }
  };
  draw(readHint());
  return {
    setSignedIn(signedIn) {
      writeHint(signedIn);
      draw(signedIn);
    },
  };
}
